import { useCallback, useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { AUTH_TOKEN_NAME, getAuthKey } from '../api/auth-utils';

export const httpMethods = {
    get: 'get',
    post: 'post',
    put: 'put',
    delete: 'delete'
};

// Cache implementation
const requestCache = new Map();

const generateCacheKey = (method, url, params, payload) => {
    return JSON.stringify({
        method,
        url,
        params: params || {},
        payload: payload || {}
    });
};

const isCacheValid = (cachedItem, cacheTime = 5 * 60 * 1000) => {
    return Date.now() - cachedItem.timestamp < cacheTime;
};

const clearExpiredCache = () => {
    const now = Date.now();
    for (const [key, value] of requestCache.entries()) {
        if (now - value.timestamp > (value.cacheTime || 5 * 60 * 1000)) {
            requestCache.delete(key);
        }
    }
};

/**
 * Custom hook for fetching data with caching, cancellation, and retry support
 *
 * Features:
 * - Request memoization with configurable cache time
 * - Automatic request cancellation on unmount
 * - Retry functionality with abort/retry support
 * - Loading states and error handling
 * - Conditional request execution
 *
 * @param {Object} config - Configuration object
 * @param {string} config.method - HTTP method (GET, POST, etc.)
 * @param {string} config.url - Request URL
 * @param {Object} config.params - Query parameters
 * @param {Object} config.payload - Request body data
 * @param {Function} config.responseHandler - Callback for successful response processing
 * @param {boolean} config.initialCondition - Condition to control initial request execution
 * @param {Function} config.processingSetter - Callback for loading state updates
 * @param {boolean} config.repeat - Flag to trigger request repetition
 * @param {Function} config.onRepeat - Callback executed on repeat
 * @param {Function} config.onRequestError - Callback for request errors; gets the axios error
 * @param {boolean} config.fullResp - Flag to return full response instead of just content
 * @param {boolean} config.isDisabled - Flag to disable request execution
 * @param {boolean} config.enableCache - Enable request caching (default: false)
 * @param {number} config.cacheTime - Cache validity time in milliseconds (default: 5 minutes)
 * @param {number} [config.timeout] - Request timeout in milliseconds (default: none); a timeout is a request error
 *
 * @returns {Object} - { processing, data, error, handleCancel, handleRepeat }
 */
// eslint-disable-next-line tpe/max-component-size -- TODO: legacy large component (baseline)
export const useFetchData = ({
    method,
    url,
    params,
    payload = null,
    responseHandler,
    initialCondition,
    processingSetter,
    repeat,
    onRepeat,
    onRequestError,
    fullResp,
    isDisabled,
    enableCache = false,
    cacheTime = 5 * 60 * 1000, // 5 minutes default
    timeout
}) => {
    const [data, setData] = useState(null);
    const [isInit, setInit] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(false);

    const cancelSourceRef = useRef(null);

    const mountedRef = useRef(true);

    // Callbacks are read through refs: callers often pass inline functions, and having them in deps
    // would re-create handleRepeat on every render (re-firing the repeat effect while `repeat` stays true)
    // and re-run the request effect. Refs also make a late response call the latest handler.
    const responseHandlerRef = useRef(responseHandler);
    const onRequestErrorRef = useRef(onRequestError);
    const onRepeatRef = useRef(onRepeat);
    const processingSetterRef = useRef(processingSetter);
    responseHandlerRef.current = responseHandler;
    onRequestErrorRef.current = onRequestError;
    onRepeatRef.current = onRepeat;
    processingSetterRef.current = processingSetter;

    const abortCurrentRequest = useCallback(() => {
        if (!cancelSourceRef.current) {
            return;
        }

        cancelSourceRef.current.cancel();
        cancelSourceRef.current = null;
    }, []);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            abortCurrentRequest();
            // Only on unmount: resetting whenever processingSetter changed identity dropped `processing`
            // to false in the middle of a request for callers passing an inline setter
            processingSetterRef.current?.(false);
        };
    }, [abortCurrentRequest]);

    // Memoized repeat handler
    const handleRepeat = useCallback(() => {
        abortCurrentRequest();

        if (isDisabled) {
            return;
        }

        setProcessing(false);
        setInit(true);
        setData(null);
        setError(false);
        onRepeatRef.current?.();
    }, [abortCurrentRequest, isDisabled]);

    // Memoized cancel handler
    const handleCancel = useCallback(() => {
        abortCurrentRequest();
        setInit(false);
        setProcessing(false);
    }, [abortCurrentRequest]);

    // Effect for repeat triggers
    useEffect(() => {
        if (!repeat || isDisabled) {
            return;
        }

        handleRepeat();
    }, [repeat, isDisabled, handleRepeat]);

    // Initialization effect
    useEffect(() => {
        // If disabled or initialCondition is explicitly false, don't initialize
        if (isDisabled || initialCondition === false) {
            return;
        }

        // If initialCondition is undefined or true, initialize
        if (initialCondition === undefined || !!initialCondition) {
            setInit(true);
            setData(null);
        }
    }, [initialCondition, isDisabled]);
    // Processing state effect
    useEffect(() => {
        if (typeof processing !== 'boolean' || !processingSetter || typeof processingSetter !== 'function') {
            return;
        }
        processingSetter(processing);
    }, [processing, processingSetter]);

    // Error logging effect
    useEffect(() => {
        if (error && !processing) {
            // eslint-disable-next-line no-console
            console.error('Error on request: ', url, ' ; at time : ', new Date());
        }
    }, [error, processing, url]);

    const resolveResponse = (response, fullResp) => {
        const data = response?.data ?? null;
        if (data?.content != null) {
            return fullResp ? data : data.content;
        }

        return data;
    };

    // Main request effect with caching
    useEffect(() => {
        // Only proceed if we should initialize and not disabled
        if (!isInit || isDisabled) {
            return;
        }

        setError(false);

        // URL validation
        if (!url || url.indexOf('null') >= 0 || url.indexOf('undefined') >= 0) {
            // eslint-disable-next-line no-console
            console.error('URL contain errors: ', url);
            setInit(false);
            setError(true);

            return;
        }

        // Cache check
        if (enableCache) {
            const cacheKey = generateCacheKey(method, url, params, payload);
            const cachedItem = requestCache.get(cacheKey);

            if (cachedItem && isCacheValid(cachedItem, cacheTime)) {
                abortCurrentRequest();
                setProcessing(false);
                setInit(false);
                responseHandlerRef.current?.(cachedItem.data);
                setData(cachedItem.data);

                return;
            }

            // Clear expired cache entries periodically
            if (Math.random() < 0.1) {
                // 10% chance to cleanup
                clearExpiredCache();
            }
        }

        setInit(false);
        setProcessing(true);

        abortCurrentRequest();

        const cancelSource = axios.CancelToken.source();
        cancelSourceRef.current = cancelSource;

        const requestConfig = {
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                [AUTH_TOKEN_NAME]: getAuthKey()
            },
            method: method,
            url: url,
            params: params,
            data: payload,
            cancelToken: cancelSource.token,
            ...(timeout ? { timeout } : {})
        };

        if (!mountedRef.current) {
            setInit(false);

            return;
        }

        axios(requestConfig)
            .then(response => {
                if (!mountedRef.current || cancelSourceRef.current !== cancelSource) {
                    return;
                }

                cancelSourceRef.current = null;
                setProcessing(false);
                setInit(false);
                const resp = resolveResponse(response, fullResp);
                if (enableCache) {
                    const cacheKey = generateCacheKey(method, url, params, payload);
                    requestCache.set(cacheKey, { data: resp, timestamp: Date.now(), cacheTime });
                }
                responseHandlerRef.current?.(resp);
                setData(resp);
            })
            .catch(err => {
                if (axios.isCancel(err) || !mountedRef.current || cancelSourceRef.current !== cancelSource) {
                    return;
                }

                cancelSourceRef.current = null;
                setProcessing(false);
                setInit(false);
                setError(true);

                // eslint-disable-next-line no-console
                console.error('Request error:', err);
                onRequestErrorRef.current?.(err);
            });
    }, [
        isInit,
        isDisabled,
        payload,
        params,
        url,
        method,
        fullResp,
        enableCache,
        cacheTime,
        timeout,
        abortCurrentRequest
    ]);

    return { processing: isInit || processing, data, error, handleCancel, handleRepeat };
};
